import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Clock, Download, Edit, Trash2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Loader2, Plus, Printer, Camera, X, Search } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const AttendancePage = () => {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [searchName, setSearchName] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", note: "" });
  const [addForm, setAddForm] = useState({ 
    employee_id: "", 
    date: new Date().toISOString().split("T")[0], 
    clock_in: "", 
    clock_out: "", 
    status: "present",
    note: "" 
  });
  const [saving, setSaving] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printTarget, setPrintTarget] = useState("all");
  const [printLoading, setPrintLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: "", code: "", address: "" });
  const [showFaceImageDialog, setShowFaceImageDialog] = useState(false);
  const [selectedFaceImage, setSelectedFaceImage] = useState({ image: null, type: "", name: "", time: "" });
  const printRef = useRef(null);
  const [showPastRecords, setShowPastRecords] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    fetchRecords();
    setShowPastRecords(false);
  }, [selectedYear, selectedMonth, selectedEmployee]);

  const fetchCompanyInfo = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      const company = res.data.company || {};
      setCompanyInfo({
        name: company.name || "",
        code: company.code || "",
        address: company.address || "",
        phone: company.phone || ""
      });
    } catch (error) {
      console.error("Failed to fetch company info");
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(res.data);
    } catch (error) {
      console.error("Failed to fetch employees");
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = `${API}/attendance?year=${selectedYear}&month=${selectedMonth}`;
      if (selectedEmployee !== "all") {
        url += `&employee_id=${selectedEmployee}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data);
    } catch (error) {
      toast.error("勤怠記録の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/attendance/export-csv?year=${selectedYear}&month=${selectedMonth}`,
        { 
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance_${selectedYear}_${selectedMonth.toString().padStart(2, "0")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSVをダウンロードしました");
    } catch (error) {
      toast.error("CSVエクスポートに失敗しました");
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditForm({
      clock_in: record.clock_in || "",
      clock_out: record.clock_out || "",
      note: record.note || ""
    });
    setShowEditDialog(true);
  };

  const saveEdit = async () => {
    if (!editingRecord) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/attendance/${editingRecord.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("記録を更新しました");
      setShowEditDialog(false);
      fetchRecords();
    } catch (error) {
      toast.error("更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`${record.employee_name}の${record.date}の記録を削除しますか？`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/attendance/${record.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("記録を削除しました");
      fetchRecords();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  const handleAdd = () => {
    // フィルターで選択されている従業員があれば、それをデフォルトにする
    const defaultEmployeeId = selectedEmployee !== "all" 
      ? selectedEmployee 
      : (employees.length > 0 ? employees[0].id : "");
    
    setAddForm({
      employee_id: defaultEmployeeId,
      date: new Date().toISOString().split("T")[0],
      clock_in: "09:00",
      clock_out: "18:00",
      status: "present",
      note: ""
    });
    setShowAddDialog(true);
  };

  const saveAdd = async () => {
    if (!addForm.employee_id) {
      toast.error("従業員を選択してください");
      return;
    }
    if (!addForm.date) {
      toast.error("日付を入力してください");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/attendance`, addForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("記録を追加しました");
      setShowAddDialog(false);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "追加に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    setPrintTarget(selectedEmployee);
    setShowPrintDialog(true);
  };

  const executePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("ポップアップがブロックされています");
      return;
    }

    // Filter records based on print target
    let printRecords = records;
    let title = `勤怠記録 ${selectedYear}年${selectedMonth}月`;
    
    if (printTarget !== "all") {
      printRecords = records.filter(r => r.employee_id === printTarget);
      const emp = employees.find(e => e.id === printTarget);
      title = `${emp?.name || '従業員'} - ${title}`;
    }

    // Group by employee with full employee info
    const byEmployee = {};
    printRecords.forEach(r => {
      if (!byEmployee[r.employee_id]) {
        const emp = employees.find(e => e.id === r.employee_id) || {};
        byEmployee[r.employee_id] = {
          name: r.employee_name,
          code: r.employee_code,
          department: emp.department || "",
          position: emp.position || "",
          records: []
        };
      }
      byEmployee[r.employee_id].records.push(r);
    });

    // Generate HTML - A4 size optimized (210mm x 297mm)
    const generateEmployeePage = (emp) => {
      // Create a full month calendar with all days
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
      
      // Map records by date
      const recordMap = {};
      emp.records.forEach(r => {
        recordMap[r.date] = r;
      });
      
      const totalWork = emp.records.reduce((s, r) => s + (r.work_hours || 0), 0);
      const totalOvertime = emp.records.reduce((s, r) => s + (r.overtime_hours || 0), 0);
      const workDays = emp.records.filter(r => r.clock_in && r.clock_out).length;
      const paidLeaveDays = emp.records.filter(r => r.status === 'paid_leave').length;
      const absentDays = emp.records.filter(r => r.status === 'absent').length;
      
      // Generate rows for all days
      let rows = '';
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const date = new Date(selectedYear, selectedMonth - 1, day);
        const dayOfWeek = date.getDay();
        const dayName = dayNames[dayOfWeek];
        const r = recordMap[dateStr];
        
        const rowClass = dayOfWeek === 0 ? 'sunday' : dayOfWeek === 6 ? 'weekend' : '';
        const overtimeClass = r?.overtime_hours > 0 ? 'overtime' : '';
        const statusText = r ? (r.status === 'paid_leave' ? '有給' : r.status === 'absent' ? '欠勤' : r.status === 'half_day' ? '半休' : '') : '';
        
        // 顔写真の生成
        const clockInPhoto = r?.clock_in_face_image ? `<img src="${r.clock_in_face_image}" class="face-photo" alt="出勤" />` : '';
        const clockOutPhoto = r?.clock_out_face_image ? `<img src="${r.clock_out_face_image}" class="face-photo" alt="退勤" />` : '';
        
        rows += `
          <tr class="${rowClass}">
            <td class="date-col">${day}</td>
            <td class="day-col">${dayName}</td>
            <td class="time-col">${r?.clock_in || ''}</td>
            <td class="photo-col">${clockInPhoto}</td>
            <td class="time-col">${r?.clock_out || ''}</td>
            <td class="photo-col">${clockOutPhoto}</td>
            <td class="hours-col">${r?.work_hours ? r.work_hours.toFixed(1) : ''}</td>
            <td class="hours-col ${overtimeClass}">${r?.overtime_hours ? r.overtime_hours.toFixed(1) : ''}</td>
            <td class="status-col">${statusText}</td>
            <td class="note-col">${r?.note || ''}</td>
          </tr>
        `;
      }
      
      return `
        <div class="page">
          <div class="company-header">
            <div class="company-name">${companyInfo.name || ''}</div>
            ${companyInfo.address ? `<div class="company-address">${companyInfo.address}</div>` : ''}
          </div>
          <div class="card-header">
            <div>
              <div class="card-title">勤務表（タイムカード）</div>
              <div class="card-subtitle">社員コード: ${emp.code}</div>
              ${emp.department ? `<div class="card-subtitle">所属: ${emp.department}</div>` : ''}
              ${emp.position ? `<div class="card-subtitle">役職: ${emp.position}</div>` : ''}
            </div>
            <div>
              <div class="card-period">${selectedYear}年${selectedMonth}月</div>
              <div class="card-employee-name">氏名: ${emp.name}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th class="date-col">日</th>
                <th class="day-col">曜</th>
                <th class="time-col">出勤</th>
                <th class="photo-col">写真</th>
                <th class="time-col">退勤</th>
                <th class="photo-col">写真</th>
                <th class="hours-col">労働h</th>
                <th class="hours-col">残業h</th>
                <th class="status-col">区分</th>
                <th class="note-col">備考</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
          
          <div class="summary-box">
            <div class="summary-item">
              <div class="summary-label">出勤日数</div>
              <div class="summary-value">${workDays}日</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">有給日数</div>
              <div class="summary-value">${paidLeaveDays}日</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">欠勤日数</div>
              <div class="summary-value">${absentDays}日</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">総労働時間</div>
              <div class="summary-value">${totalWork.toFixed(1)}h</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">総残業時間</div>
              <div class="summary-value overtime">${totalOvertime.toFixed(1)}h</div>
            </div>
          </div>
          
          <div class="signature-area">
            <div class="signature-box">承認</div>
            <div class="signature-box">確認</div>
            <div class="signature-box">本人</div>
          </div>
        </div>
      `;
    };

    const employeePages = Object.values(byEmployee).map(emp => generateEmployeePage(emp)).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 8mm;
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Hiragino Sans', 'Meiryo', sans-serif; 
            font-size: 8px;
            line-height: 1.1;
          }
          .page {
            width: 194mm;
            height: 281mm;
            padding: 3mm;
            page-break-after: always;
            overflow: hidden;
          }
          .page:last-child {
            page-break-after: auto;
          }
          .company-header {
            text-align: center;
            margin-bottom: 2mm;
            padding-bottom: 1mm;
            border-bottom: 1px solid #999;
          }
          .company-name {
            font-size: 12px;
            font-weight: bold;
            color: #333;
          }
          .company-address {
            font-size: 8px;
            color: #666;
          }
          .card-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border: 2px solid #333;
            padding: 2mm;
            margin-bottom: 2mm;
          }
          .card-title {
            font-size: 12px;
            font-weight: bold;
          }
          .card-subtitle {
            font-size: 8px;
            color: #666;
            margin-top: 1px;
          }
          .card-period {
            text-align: right;
            font-size: 11px;
            font-weight: bold;
          }
          .card-employee-name {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            margin-top: 1px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7px;
          }
          th, td {
            border: 1px solid #999;
            padding: 0.8mm 1mm;
            text-align: center;
          }
          th {
            background: #e8e8e8;
            font-weight: bold;
            font-size: 7px;
            height: 5mm;
          }
          td {
            font-size: 7px;
            height: 6.5mm;
            vertical-align: middle;
          }
          .date-col { width: 7mm; }
          .day-col { width: 6mm; }
          .time-col { width: 14mm; font-family: monospace; font-size: 7px; }
          .photo-col { width: 8mm; padding: 0.5mm; }
          .hours-col { width: 10mm; }
          .status-col { width: 8mm; font-size: 6px; }
          .note-col { width: auto; text-align: left; font-size: 6px; max-width: 30mm; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .face-photo { 
            width: 6mm; 
            height: 6mm; 
            object-fit: cover; 
            border-radius: 0.5mm;
            display: block;
            margin: 0 auto;
          }
          .weekend { background: #f5f5f5; }
          .sunday { background: #ffe8e8; }
          .overtime { color: #dc2626; font-weight: bold; }
          .summary-box {
            display: flex;
            justify-content: space-around;
            border: 2px solid #333;
            padding: 2mm;
            margin-top: 2mm;
          }
          .summary-item {
            text-align: center;
          }
          .summary-label {
            font-size: 7px;
            color: #666;
            margin-bottom: 0.5mm;
          }
          .summary-value {
            font-size: 11px;
            font-weight: bold;
          }
          .signature-area {
            display: flex;
            justify-content: flex-end;
            gap: 8mm;
            margin-top: 3mm;
          }
          .signature-box {
            width: 22mm;
            height: 16mm;
            border: 1px solid #999;
            text-align: center;
            font-size: 7px;
            padding-top: 12mm;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .page { height: auto; }
          }
        </style>
      </head>
      <body>
        ${employeePages}
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    setShowPrintDialog(false);
  };

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const q = searchName.trim().toLowerCase();
  const filteredRecords = q
    ? records.filter(
        (r) =>
          (r.employee_name || "").toLowerCase().includes(q) ||
          (r.employee_code || "").toLowerCase().includes(q)
      )
    : records;

  const groupedRecords = filteredRecords.reduce((acc, record) => {
    if (!acc[record.date]) acc[record.date] = [];
    acc[record.date].push(record);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedRecords).sort((a, b) => b.localeCompare(a));

  // 当日を最上部に固定し、それ以前の記録は「続きを見る」で展開する
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const todayDates = sortedDates.filter((d) => d === todayStr);
  const pastDates = sortedDates.filter((d) => d !== todayStr);
  const hasToday = todayDates.length > 0;
  const visibleDates = hasToday
    ? (showPastRecords ? [...todayDates, ...pastDates] : todayDates)
    : sortedDates;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold mb-1">勤怠記録</h1>
          <p className="text-slate-400 text-sm">打刻履歴の確認・追加・編集・削除</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleAdd} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            新規追加
          </Button>
          <Button onClick={handlePrint} size="sm" variant="outline">
            <Printer className="w-4 h-4 mr-2" />
            印刷
          </Button>
          <Button onClick={handleExport} size="sm" variant="outline">
            <Download className="w-4 h-4 mr-2" />
            CSVエクスポート
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} className="border-slate-600">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="min-w-[120px] text-center font-semibold">
                {selectedYear}年{selectedMonth}月
              </div>
              <Button variant="outline" size="icon" onClick={() => changeMonth(1)} className="border-slate-600">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600">
                <SelectValue placeholder="全従業員" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全従業員</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="従業員名で検索"
                className="pl-9 bg-slate-700 border-slate-600"
                data-testid="attendance-search-input"
              />
              {searchName && (
                <button
                  onClick={() => setSearchName("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  data-testid="attendance-search-clear"
                  aria-label="検索をクリア"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Records */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : filteredRecords.length === 0 ? (
        <Card className="p-12 text-center bg-slate-800 border-slate-700">
          <Clock className="w-12 h-12 mx-auto mb-4 text-slate-500" />
          <h3 className="text-lg font-semibold mb-2">記録がありません</h3>
          <p className="text-slate-400">
            {q ? `「${searchName}」に一致する従業員の記録がありません` : "選択した期間に勤怠記録がありません"}
          </p>
        </Card>
      ) : (
        <Card className="bg-slate-800 border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">日付</th>
                  <th className="px-4 py-3 text-left font-medium">社員</th>
                  <th className="px-4 py-3 text-left font-medium">出勤</th>
                  <th className="px-4 py-3 text-left font-medium">退勤</th>
                  <th className="px-4 py-3 text-left font-medium">労働</th>
                  <th className="px-4 py-3 text-left font-medium">残業</th>
                  <th className="px-4 py-3 text-left font-medium">状態</th>
                  <th className="px-4 py-3 text-center font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {visibleDates.map(date => (
                  groupedRecords[date].map((record, i) => (
                    <tr key={record.id} className="hover:bg-slate-700/50">
                      {i === 0 && (
                        <td rowSpan={groupedRecords[date].length} className="px-4 py-3 font-medium bg-slate-750 align-top">
                          <div className="flex items-center gap-2 sticky top-16">
                            <span>{date}</span>
                            {date === todayStr && (
                              <Badge className="bg-emerald-600 text-[10px] px-1.5 py-0" data-testid="today-badge">本日</Badge>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="font-medium">{record.employee_name}</div>
                        <div className="text-xs text-slate-400">{record.employee_code}</div>
                      </td>
                      <td className="px-4 py-3">
                        {record.clock_in ? (
                          <div className="flex items-center gap-2">
                            {record.clock_in_face_image && (
                              <button
                                onClick={() => setSelectedFaceImage({ 
                                  image: record.clock_in_face_image, 
                                  type: "出勤", 
                                  name: record.employee_name,
                                  time: record.clock_in 
                                }) || setShowFaceImageDialog(true)}
                                className="w-8 h-8 rounded-full overflow-hidden border-2 border-emerald-500 hover:border-emerald-400 transition-colors flex-shrink-0"
                                title="出勤時の顔写真を表示"
                              >
                                <img src={record.clock_in_face_image} alt="出勤" className="w-full h-full object-cover" />
                              </button>
                            )}
                            <div>
                              <span className="text-emerald-400 font-medium">{record.clock_in}</span>
                              {record.clock_in_raw && record.clock_in_raw !== record.clock_in && (
                                <div className="text-xs text-slate-500">実: {record.clock_in_raw}</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500">--:--</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {record.clock_out ? (
                          <div className="flex items-center gap-2">
                            {record.clock_out_face_image && (
                              <button
                                onClick={() => setSelectedFaceImage({ 
                                  image: record.clock_out_face_image, 
                                  type: "退勤", 
                                  name: record.employee_name,
                                  time: record.clock_out 
                                }) || setShowFaceImageDialog(true)}
                                className="w-8 h-8 rounded-full overflow-hidden border-2 border-blue-500 hover:border-blue-400 transition-colors flex-shrink-0"
                                title="退勤時の顔写真を表示"
                              >
                                <img src={record.clock_out_face_image} alt="退勤" className="w-full h-full object-cover" />
                              </button>
                            )}
                            <div>
                              <span className="text-blue-400 font-medium">{record.clock_out}</span>
                              {record.clock_out_raw && record.clock_out_raw !== record.clock_out && (
                                <div className="text-xs text-slate-500">実: {record.clock_out_raw}</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500">--:--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{record.work_hours || 0}h</td>
                      <td className="px-4 py-3">
                        {record.overtime_hours > 0 ? (
                          <span className="text-red-400 font-medium">{record.overtime_hours}h</span>
                        ) : (
                          <span className="text-slate-500">0h</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {record.clock_in && record.clock_out ? (
                          <Badge className="bg-emerald-600 text-xs">完了</Badge>
                        ) : record.clock_in ? (
                          <Badge className="bg-yellow-600 text-xs">出勤中</Badge>
                        ) : (
                          <Badge variant="outline" className="border-slate-600 text-xs">未出勤</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(record)} className="h-8 w-8 p-0">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(record)} className="h-8 w-8 p-0 text-red-400 hover:text-red-300">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ))}
                {hasToday && pastDates.length > 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-3 text-center bg-slate-800/60">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPastRecords(!showPastRecords)}
                        data-testid="toggle-past-records-button"
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        {showPastRecords ? (
                          <><ChevronUp className="w-4 h-4 mr-2" />閉じる</>
                        ) : (
                          <><ChevronDown className="w-4 h-4 mr-2" />続きを見る（他{pastDates.length}日分）</>
                        )}
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Summary */}
      {filteredRecords.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-400 mb-1">総記録数</p>
              <p className="text-2xl font-bold">{filteredRecords.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-400 mb-1">総労働時間</p>
              <p className="text-2xl font-bold">
                {filteredRecords.reduce((sum, r) => sum + (r.work_hours || 0), 0).toFixed(1)}h
              </p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-400 mb-1">総残業時間</p>
              <p className="text-2xl font-bold text-red-400">
                {filteredRecords.reduce((sum, r) => sum + (r.overtime_hours || 0), 0).toFixed(1)}h
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>勤怠記録の編集</DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-700 rounded-lg">
                <p className="font-medium">{editingRecord.employee_name}</p>
                <p className="text-sm text-slate-400">{editingRecord.date}</p>
              </div>
              <div className="space-y-2">
                <Label>出勤時刻</Label>
                <Input
                  type="time"
                  value={editForm.clock_in}
                  onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>退勤時刻</Label>
                <Input
                  type="time"
                  value={editForm.clock_out}
                  onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>備考</Label>
                <Input
                  value={editForm.note}
                  onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  placeholder="備考を入力..."
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1 border-slate-600">
                  キャンセル
                </Button>
                <Button onClick={saveEdit} className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>勤怠記録の新規追加</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>従業員</Label>
              <Select value={addForm.employee_id} onValueChange={(v) => setAddForm({ ...addForm, employee_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="従業員を選択" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>日付</Label>
              <Input
                type="date"
                value={addForm.date}
                onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>出勤時刻</Label>
                <Input
                  type="time"
                  value={addForm.clock_in}
                  onChange={(e) => setAddForm({ ...addForm, clock_in: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>退勤時刻</Label>
                <Input
                  type="time"
                  value={addForm.clock_out}
                  onChange={(e) => setAddForm({ ...addForm, clock_out: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>ステータス</Label>
              <Select value={addForm.status} onValueChange={(v) => setAddForm({ ...addForm, status: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">出勤</SelectItem>
                  <SelectItem value="paid_leave">有給休暇</SelectItem>
                  <SelectItem value="absent">欠勤</SelectItem>
                  <SelectItem value="half_day">半休</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>備考</Label>
              <Input
                value={addForm.note}
                onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
                placeholder="備考を入力..."
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1 border-slate-600">
                キャンセル
              </Button>
              <Button onClick={saveAdd} className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "追加"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              勤怠記録の印刷
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-700 rounded-lg">
              <p className="text-sm text-slate-400 mb-1">印刷対象期間</p>
              <p className="font-medium text-lg">{selectedYear}年{selectedMonth}月</p>
            </div>
            
            <div className="space-y-2">
              <Label>印刷対象</Label>
              <Select value={printTarget} onValueChange={setPrintTarget}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全従業員</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-slate-700/50 rounded-lg text-sm text-slate-400">
              <p>• 新しいウィンドウで印刷プレビューが開きます</p>
              <p>• 各従業員ごとにタイムカードが印刷されます</p>
              <p>• 出勤日数、労働時間、残業時間の集計付き</p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPrintDialog(false)} className="flex-1 border-slate-600">
                キャンセル
              </Button>
              <Button onClick={executePrint} className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" />
                印刷する
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Face Image Dialog */}
      <Dialog open={showFaceImageDialog} onOpenChange={setShowFaceImageDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              {selectedFaceImage.type}時の顔写真
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-lg font-medium mb-1">{selectedFaceImage.name}</p>
              <p className="text-sm text-slate-400">{selectedFaceImage.time}</p>
            </div>
            {selectedFaceImage.image && (
              <div className="flex justify-center">
                <img 
                  src={selectedFaceImage.image} 
                  alt={`${selectedFaceImage.type}時の顔写真`}
                  className="w-48 h-48 object-cover rounded-xl border-4 border-slate-600"
                />
              </div>
            )}
            <Button onClick={() => setShowFaceImageDialog(false)} className="w-full">
              閉じる
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePage;
